import Phaser from 'phaser';
import { GAME_WIDTH, IS_PORTRAIT_LAYOUT } from '../core/Config';
import { GameSettings } from '../core/GameSettings';

export class ChoiceUI {
  private readonly scene: Phaser.Scene;
  private readonly outer: Phaser.GameObjects.Rectangle;
  private readonly inner: Phaser.GameObjects.Rectangle;
  private readonly title: Phaser.GameObjects.Text;
  private readonly texts: Phaser.GameObjects.Text[] = [];
  private readonly bars: Phaser.GameObjects.Rectangle[] = [];
  private readonly baseX: number;
  private readonly textX: number;
  private readonly rowStartY: number;
  private readonly rowGap: number;
  private choices: string[] = [];
  private currentIndex = 0;
  private onChoose?: (index: number) => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const mobile = IS_PORTRAIT_LAYOUT;
    this.baseX = mobile ? GAME_WIDTH / 2 : GAME_WIDTH - 260;
    this.textX = mobile ? 82 : this.baseX - 152;
    this.rowStartY = mobile ? 720 : 266;
    this.rowGap = mobile ? 94 : 44;
    const panelWidth = mobile ? GAME_WIDTH - 48 : 420;
    const panelHeight = mobile ? 470 : 250;
    const panelY = mobile ? 878 : 312;

    this.outer = scene.add
      .rectangle(this.baseX, panelY, panelWidth, panelHeight, 0x172019, 0.98)
      .setStrokeStyle(mobile ? 5 : 3, 0xe8c768, 0.96)
      .setScrollFactor(0)
      .setDepth(10010)
      .setVisible(false);
    this.inner = scene.add
      .rectangle(this.baseX, panelY, panelWidth - 26, panelHeight - 26, 0x0c120f, 0.98)
      .setStrokeStyle(2, 0x8ebc98, 0.72)
      .setScrollFactor(0)
      .setDepth(10011)
      .setVisible(false);
    this.title = scene.add
      .text(mobile ? 70 : this.baseX - 156, mobile ? 660 : 210, '선택', {
        fontSize: mobile ? '34px' : '22px',
        color: '#fff0b5',
        fontStyle: 'bold',
      })
      .setScrollFactor(0)
      .setDepth(10012)
      .setVisible(false);

    for (let index = 0; index < 4; index += 1) {
      const y = this.rowStartY + index * this.rowGap;
      const bar = scene.add
        .rectangle(this.baseX, y, mobile ? panelWidth - 72 : 354, mobile ? 76 : 34, 0x3d5944, 0.36)
        .setStrokeStyle(mobile ? 2 : 1, 0x95b99d, 0.5)
        .setScrollFactor(0)
        .setDepth(10011)
        .setVisible(false)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          if (!this.choices[index]) return;
          this.currentIndex = index;
          this.render();
          this.onChoose?.(index);
        });
      const text = scene.add
        .text(this.textX, y - (mobile ? 24 : 14), '', {
          fontSize: mobile ? '31px' : '23px',
          color: '#eef4ec',
          wordWrap: { width: mobile ? panelWidth - 150 : 330 },
        })
        .setScrollFactor(0)
        .setDepth(10012)
        .setVisible(false);
      this.bars.push(bar);
      this.texts.push(text);
    }
  }

  setOnChoose(handler?: (index: number) => void) {
    this.onChoose = handler;
  }

  show(choices: string[], selectedIndex: number) {
    this.choices = choices.slice(0, 4);
    this.currentIndex = selectedIndex;
    this.outer.setVisible(true);
    this.inner.setVisible(true);
    this.title.setVisible(true);
    this.render();
  }

  hide() {
    this.outer.setVisible(false);
    this.inner.setVisible(false);
    this.title.setVisible(false);
    this.choices = [];
    this.texts.forEach((text) => {
      this.scene.tweens.killTweensOf(text);
      text.setVisible(false).setX(this.textX);
    });
    this.bars.forEach((bar) => bar.setVisible(false));
  }

  isVisible() {
    return this.outer.visible;
  }

  private render() {
    this.texts.forEach((text, index) => {
      const label = this.choices[index] ?? '';
      const selected = this.currentIndex === index;
      const selectedOffset = selected && GameSettings.get('uiMotion') ? (IS_PORTRAIT_LAYOUT ? 10 : 6) : 0;
      text
        .setText(label ? `${selected ? '> ' : '  '}${label}` : '')
        .setColor(selected ? '#ffe391' : '#eef4ec')
        .setX(this.textX + selectedOffset)
        .setVisible(Boolean(label));
      this.bars[index]
        .setVisible(Boolean(label))
        .setAlpha(selected ? 1 : 0.72)
        .setFillStyle(selected ? 0x4d684f : 0x243329, selected ? 0.92 : 0.66)
        .setStrokeStyle(IS_PORTRAIT_LAYOUT ? 2 : 1, selected ? 0xe8c768 : 0x6f8d75, selected ? 0.9 : 0.5);

      this.scene.tweens.killTweensOf(text);
      if (selected && GameSettings.get('uiMotion')) {
        this.scene.tweens.add({
          targets: text,
          x: this.textX + selectedOffset + (IS_PORTRAIT_LAYOUT ? 8 : 6),
          duration: 320,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut',
        });
      }
    });
  }
}
